<script lang="ts" setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Swiper from 'swiper'
import type { HomePageConfig } from '../configs/types'
import type { DramaCharacter } from '../configs/drama'
import 'swiper/css'

const props = defineProps<{
  value: boolean
  config: HomePageConfig
  characters: DramaCharacter[]
  /** 打开时定位到的人物下标 */
  index: number
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
}>()

const swiperEl = ref<HTMLElement | null>(null)
let swiper: Swiper | null = null

function destroySwiper() {
  // 保留样式，避免关闭动画期间卡片跳回第一张
  swiper?.destroy(true, false)
  swiper = null
}

watch(() => props.value, async (visible) => {
  if (!visible) {
    destroySwiper()
    return
  }
  await nextTick()
  if (!swiperEl.value)
    return
  swiper = new Swiper(swiperEl.value, {
    initialSlide: props.index,
  })
})

onBeforeUnmount(destroySwiper)

function onInput(value: boolean) {
  emit('input', value)
}
</script>

<template>
  <BasicPopup :value="value" @input="onInput">
    <div class="character-popup">
      <i class="popup-bg" />
      <img src="/images/vowpath/home/characters/deco-stars-left.png" class="deco deco-stars-left">
      <img src="/images/vowpath/home/characters/deco-stars-right.png" class="deco deco-stars-right">
      <img src="/images/vowpath/home/characters/deco-compass.png" class="deco deco-compass">
      <img src="/images/vowpath/home/characters/deco-constellation.png" class="deco deco-constellation">

      <h3 class="popup-title">
        {{ config.texts.characterHighlights }}
      </h3>

      <div ref="swiperEl" class="swiper popup-swiper">
        <div class="swiper-wrapper">
          <div v-for="(character, i) in characters" :key="i" class="swiper-slide">
            <div class="portrait">
              <img :src="character.avatar" class="portrait-img">
              <i class="portrait-shade" />
              <img src="/images/vowpath/home/characters/frame.png" class="portrait-frame">
              <div class="portrait-info">
                <p class="portrait-name">
                  {{ character.name }}
                </p>
                <div v-if="character.tags && character.tags.length" class="flex flex-wrap gap-10px mt-10px">
                  <span v-for="tag in character.tags" :key="tag" class="portrait-tag">{{ tag }}</span>
                </div>
                <p class="portrait-desc">
                  {{ character.desc }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.character-popup {
  position: relative;
  height: 505px;
  border-radius: 26px 26px 0 0;
  overflow: hidden;
}

.popup-bg {
  position: absolute;
  inset: 0;
  opacity: 0.96;
  background:
    radial-gradient(circle at 0 0, rgba(236, 193, 255, 0.55) 0%, rgba(236, 193, 255, 0) 38%),
    linear-gradient(152deg, rgba(199, 81, 244, 1) 0%, rgba(101, 22, 127, 1) 52%, rgba(40, 6, 65, 1) 85%, rgba(36, 0, 63, 1) 100%);
}

.deco {
  position: absolute;
  pointer-events: none;
}

.deco-stars-left {
  left: 0;
  top: 40px;
  width: 73px;
  height: 173px;
}

.deco-stars-right {
  right: 0;
  top: 0;
  width: 124px;
  height: 173px;
}

.deco-compass {
  right: 0;
  bottom: 0;
  width: 186px;
  height: 312px;
}

.deco-constellation {
  left: 20px;
  bottom: 0;
  width: 234px;
  height: 84px;
}

.popup-title {
  position: relative;
  padding-top: 18px;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  color: #ffffff;
  text-align: center;
}

.popup-swiper {
  margin-top: 35px;
}

.portrait {
  position: relative;
  width: 281px;
  height: 386px;
  margin: 0 auto;
}

.portrait-img,
.portrait-shade {
  position: absolute;
  left: 5px;
  top: 5px;
  width: 271px;
  height: 376px;
}

.portrait-img {
  object-fit: cover;
}

.portrait-shade {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.75) 100%);
}

.portrait-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.portrait-info {
  position: absolute;
  left: 21px;
  right: 21px;
  bottom: 29px;
}

.portrait-name {
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-size: 24px;
  line-height: 30px;
  color: #ffffff;
}

.portrait-tag {
  height: 26px;
  padding: 0 16px;
  border: 1px solid rgba(255, 230, 162, 0.3);
  border-radius: 13px;
  background: rgba(255, 230, 162, 0.15);
  font-size: 11px;
  line-height: 24px;
  color: rgba(255, 255, 255, 0.7);
}

.portrait-desc {
  margin-top: 14px;
  font-size: 10.5px;
  line-height: 15.5px;
  color: rgba(255, 255, 255, 0.6);
}
</style>
